import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { onboardingKeys } from '@/data/query-keys';

export interface SectionData {
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  data?: any;
  completedAt?: string;
}

export interface OnboardingState {
  currentSection: number;
  totalSections: number;
  sections: Record<string, SectionData>;
  isComplete: boolean;
  isSaving: boolean;
}

interface OnboardingContextType {
  state: OnboardingState;
  goToSection: (section: number) => void;
  nextSection: () => void;
  previousSection: () => void;
  completeSection: (sectionKey: string, data?: any) => Promise<void>;
  skipSection: (sectionKey: string) => Promise<void>;
  updateSectionData: (sectionKey: string, data: any) => Promise<void>;
  finishOnboarding: () => Promise<void>;
  percentComplete: number;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// ARCHIVED: 'addons' removed for MVP
const SECTION_KEYS = [
  'business_profile',      // 1. Business Profile
  'bank_accounts',         // 2. Bank Account Setup & Training
  'inventory_list_setup',  // 3. Inventory List Setup
  'vendors',               // 4. COGS Vendor Setup & Training
  'prep_menu_recipes',     // 5. Prep & Menu Item Recipes (with Menu Costing)
  'pos_integration',       // 6. POS Integration & Training
  'menu_pricing',          // 7. Menu Pricing (Campaigns, Summary, per-platform pricing)
  'payroll',               // 8. Payroll
] as const;

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { data: companyData } = useCompanyInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<OnboardingState>({
    currentSection: 1,
    totalSections: SECTION_KEYS.length,
    sections: {},
    isComplete: false,
    isSaving: false,
  });

  // Load progress from database
  useEffect(() => {
    if (!companyData?.id) return;

    const loadProgress = async () => {
      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('*')
        .eq('company_id', companyData.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading onboarding progress:', error);
        return;
      }

      if (data) {
        const sectionsCompleted = (data.sections_completed as any) || {};
        
        // Find first incomplete section
        let firstIncomplete = 1;
        for (let i = 0; i < SECTION_KEYS.length; i++) {
          const sectionKey = SECTION_KEYS[i];
          const section = sectionsCompleted[sectionKey];
          if (!section || (section.status !== 'completed' && section.status !== 'skipped')) {
            firstIncomplete = i + 1;
            break;
          }
        }
        
        // Check if all sections are done
        const allDone = SECTION_KEYS.every(key => {
          const section = sectionsCompleted[key];
          return section && (section.status === 'completed' || section.status === 'skipped');
        });
        
        setState(prev => ({
          ...prev,
          currentSection: allDone ? prev.totalSections : Math.min(firstIncomplete, data.current_section),
          sections: sectionsCompleted as Record<string, SectionData>,
          isComplete: !!data.completed_at || allDone,
        }));
        
        // If all done and not marked complete, mark as complete and redirect to Phase 2
        if (allDone && !data.completed_at) {
          // Mark Phase 1 as complete in database directly
          setTimeout(async () => {
            const { error: updateError } = await supabase
              .from('onboarding_progress')
              .update({
                completed_at: new Date().toISOString(),
                last_activity: new Date().toISOString(),
              })
              .eq('company_id', companyData.id);
            
            if (!updateError) {
              setState(prev => ({ ...prev, isComplete: true }));

              toast.success('🎉 Phase 1 complete! Redirecting to onboarding hub...');
              setTimeout(() => {
                navigate('/onboarding', { replace: true });
              }, 1500);
            }
          }, 1000);
        }
      } else {
        // Initialize progress
        const { error: insertError } = await supabase
          .from('onboarding_progress')
          .insert({
            company_id: companyData.id,
            current_section: 1,
            sections_completed: {},
          });

        if (insertError) {
          console.error('Error initializing onboarding progress:', insertError);
        }
      }
    };

    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData?.id]);

  // Auto-save progress
  const saveProgress = useCallback(async (updates: Partial<OnboardingState>) => {
    // Prefer company id from react-query, but fall back to fetching from profile after initial company creation
    let companyId = companyData?.id;

    if (!companyId) {
      companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        console.warn('Cannot save onboarding progress: company id is missing');
        return;
      }
    }

    setState(prev => ({ ...prev, isSaving: true }));

    const sectionsData = updates.sections ?? state.sections;
    
    // Create a complete data snapshot with all section data
    // Ensure we only include serializable data (no functions, circular refs, etc.)
    const dataSnapshot: Record<string, any> = {};
    Object.entries(sectionsData).forEach(([key, section]) => {
      if (section.data) {
        try {
          // Deep clone and ensure it's serializable
          dataSnapshot[key] = JSON.parse(JSON.stringify(section.data));
        } catch (e) {
          console.warn(`Failed to serialize data for section ${key}:`, e);
          // Fallback to a simple representation
          dataSnapshot[key] = typeof section.data === 'object' 
            ? { ...section.data } 
            : section.data;
        }
      }
    });
    
    // Ensure sections_completed is serializable
    let serializedSections: any;
    try {
      serializedSections = JSON.parse(JSON.stringify(sectionsData));
    } catch (e) {
      console.warn('Failed to serialize sections_completed:', e);
      serializedSections = sectionsData;
    }
    
    const payload = {
      company_id: companyId,
      current_section: updates.currentSection ?? state.currentSection,
      sections_completed: serializedSections,
      data_snapshot: dataSnapshot,
      completed_at: updates.isComplete ? new Date().toISOString() : null,
      last_activity: new Date().toISOString(),
    };

    // Log what we're saving (for debugging)
    console.log('💾 Saving onboarding progress:', {
      company_id: companyId,
      current_section: payload.current_section,
      sections_count: Object.keys(sectionsData).length,
      sections_with_data: Object.keys(dataSnapshot).length,
      data_snapshot_keys: Object.keys(dataSnapshot),
    });

    // Use upsert to handle both insert and update cases
    // Try upsert first, fallback to insert if needed
    let savedData: any = null;
    let error: any = null;

    try {
      const result = await supabase
        .from('onboarding_progress')
        .upsert(payload, {
          onConflict: 'company_id',
        })
        .select()
        .single();
      
      savedData = result.data;
      error = result.error;
    } catch (err: any) {
      // If upsert fails, try insert (in case of race condition)
      console.warn('Upsert failed, trying insert:', err);
      try {
        const insertResult = await supabase
          .from('onboarding_progress')
          .insert(payload)
          .select()
          .single();
        
        savedData = insertResult.data;
        error = insertResult.error;
      } catch (insertErr: any) {
        // If insert also fails, try update (record might exist)
        console.warn('Insert failed, trying update:', insertErr);
        const updateResult = await supabase
          .from('onboarding_progress')
          .update(payload)
          .eq('company_id', companyId)
          .select()
          .single();
        
        savedData = updateResult.data;
        error = updateResult.error;
      }
    }

    if (error) {
      console.error('❌ Error saving onboarding progress:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      toast.error('Failed to save progress');
    } else {
      console.log('✅ Onboarding progress saved successfully:', {
        id: savedData?.id,
        current_section: savedData?.current_section,
        sections_completed_count: Object.keys(savedData?.sections_completed || {}).length,
        data_snapshot_count: Object.keys(savedData?.data_snapshot || {}).length,
        last_activity: savedData?.last_activity,
      });
      queryClient.invalidateQueries({ queryKey: onboardingKeys.progress() });

      // Verify the data was actually saved by reading it back (optional, don't fail if this fails)
      try {
        const { data: verificationData, error: verifyError } = await supabase
          .from('onboarding_progress')
          .select('sections_completed, data_snapshot')
          .eq('company_id', companyId)
          .maybeSingle();

        if (!verifyError && verificationData) {
          console.log('🔍 Verification - Data in database:', {
            sections_completed: verificationData.sections_completed,
            data_snapshot: verificationData.data_snapshot,
          });
        } else if (verifyError) {
          console.warn('⚠️ Could not verify saved data:', verifyError);
        }
      } catch (verifyErr) {
        // Don't fail if verification fails
        console.warn('⚠️ Verification query failed:', verifyErr);
      }
    }

    setState(prev => ({ ...prev, isSaving: false }));
  }, [companyData?.id, state.currentSection, state.sections, queryClient]);

  const goToSection = useCallback((section: number) => {
    setState(prev => ({ ...prev, currentSection: section }));
    saveProgress({ currentSection: section });
  }, [saveProgress]);

  const finishOnboarding = useCallback(async () => {
    setState(prev => ({ ...prev, isComplete: true }));
    await saveProgress({ isComplete: true });

    toast.success('🎉 Phase 1 complete! Redirecting to onboarding hub...');
    
    // Redirect to onboarding hub after a short delay
    setTimeout(() => {
      navigate('/onboarding', { replace: true });
    }, 1500);
  }, [saveProgress, navigate]);

  const nextSection = useCallback(() => {
    setState(prev => {
      // Basic business info only includes sections 1-4 (business_profile, bank_accounts, vendors, payroll)
      const BASIC_BUSINESS_INFO_SECTIONS = 4;
      
      // Find next incomplete section (only within basic business info)
      let next = prev.currentSection + 1;
      while (next <= BASIC_BUSINESS_INFO_SECTIONS) {
        const sectionKey = SECTION_KEYS[next - 1];
        const section = prev.sections[sectionKey];
        // Skip if already completed or skipped
        if (!section || (section.status !== 'completed' && section.status !== 'skipped')) {
          break;
        }
        next++;
      }
      
      // If all basic business info sections (1-4) are done, redirect to hub
      if (next > BASIC_BUSINESS_INFO_SECTIONS) {
        const basicSectionsDone = SECTION_KEYS.slice(0, BASIC_BUSINESS_INFO_SECTIONS).every(key => {
          const section = prev.sections[key];
          return section && (section.status === 'completed' || section.status === 'skipped');
        });
        
        if (basicSectionsDone) {
          // Save progress and navigate to hub
          saveProgress({ currentSection: BASIC_BUSINESS_INFO_SECTIONS }).then(() => {
            navigate('/onboarding');
          });
          return prev;
        }
      }
      
      const finalNext = Math.min(next, BASIC_BUSINESS_INFO_SECTIONS);
      saveProgress({ currentSection: finalNext });
      return { ...prev, currentSection: finalNext };
    });
  }, [saveProgress, navigate]);

  const previousSection = useCallback(async () => {
    let targetSection = 1;
    setState(prev => {
      targetSection = Math.max(prev.currentSection - 1, 1);
      return { ...prev, currentSection: targetSection };
    });
    try {
      await saveProgress({ currentSection: targetSection });
    } catch (err) {
      console.error('Error saving progress on previous section:', err);
      toast.error('Could not save your position. Your changes are still safe.');
    }
  }, [saveProgress]);

  const completeSection = useCallback(async (sectionKey: string, data?: any) => {
    const updatedSections = {
      ...state.sections,
      [sectionKey]: {
        status: 'completed' as const,
        data,
        completedAt: new Date().toISOString(),
      },
    };

    setState(prev => ({
      ...prev,
      sections: updatedSections,
    }));

    await saveProgress({ sections: updatedSections });
    
    // Check if we're on a Phase 2 route (training modules)
    // Phase 2 routes handle their own navigation, so skip auto-navigation
    const isPhase2Route = location.pathname.includes('/onboarding/pos-training') ||
                          location.pathname.includes('/onboarding/bank-account-training') ||
                          location.pathname.includes('/onboarding/cogs-vendor');
    
    if (isPhase2Route) {
      // Don't auto-navigate on Phase 2 routes - let the component handle navigation
      return;
    }
    
    // Check if all sections are now complete
    const allDone = SECTION_KEYS.every(key => {
      const section = updatedSections[key];
      return section && (section.status === 'completed' || section.status === 'skipped');
    });

    if (allDone) {
      // Small delay to show completion message
      setTimeout(() => {
        finishOnboarding();
      }, 500);
    } else {
      // Auto-advance to next incomplete section
      setTimeout(() => {
        nextSection();
      }, 300);
    }
  }, [state.sections, saveProgress, nextSection, finishOnboarding, location.pathname]);

  const skipSection = useCallback(async (sectionKey: string) => {
    const updatedSections = {
      ...state.sections,
      [sectionKey]: {
        status: 'skipped' as const,
        completedAt: new Date().toISOString(),
      },
    };

    setState(prev => ({
      ...prev,
      sections: updatedSections,
    }));

    await saveProgress({ sections: updatedSections });
    
    // Check if we're on a Phase 2 route (training modules)
    // Phase 2 routes handle their own navigation, so skip auto-navigation
    const isPhase2Route = location.pathname.includes('/onboarding/pos-training') ||
                          location.pathname.includes('/onboarding/bank-account-training') ||
                          location.pathname.includes('/onboarding/cogs-vendor');
    
    if (isPhase2Route) {
      // Don't auto-navigate on Phase 2 routes - let the component handle navigation
      return;
    }
    
    // Check if all sections are now complete
    const allDone = SECTION_KEYS.every(key => {
      const section = updatedSections[key];
      return section && (section.status === 'completed' || section.status === 'skipped');
    });

    if (allDone) {
      // Small delay to show skip message
      setTimeout(() => {
        finishOnboarding();
      }, 500);
    } else {
      // Auto-advance to next incomplete section
      setTimeout(() => {
        nextSection();
      }, 300);
    }
  }, [state.sections, saveProgress, nextSection, finishOnboarding, location.pathname]);

  const updateSectionData = useCallback(async (sectionKey: string, data: any) => {
    const updatedSections = {
      ...state.sections,
      [sectionKey]: {
        status: 'in_progress' as const,
        data: { ...(state.sections[sectionKey]?.data || {}), ...data },
      },
    };

    setState(prev => ({
      ...prev,
      sections: updatedSections,
    }));

    await saveProgress({ sections: updatedSections });
  }, [state.sections, saveProgress]);

  // Calculate completion percentage
  const percentComplete = React.useMemo(() => {
    // If onboarding is marked as complete, return 100%
    if (state.isComplete) {
      return 100;
    }
    
    const completedCount = SECTION_KEYS.filter(key => {
      const section = state.sections[key];
      return section && (section.status === 'completed' || section.status === 'skipped');
    }).length;
    
    // If all sections are completed/skipped, return 100%
    if (completedCount === state.totalSections) {
      return 100;
    }
    
    return Math.round((completedCount / state.totalSections) * 100);
  }, [state.sections, state.totalSections, state.isComplete]);

  return (
    <OnboardingContext.Provider
      value={{
        state,
        goToSection,
        nextSection,
        previousSection,
        completeSection,
        skipSection,
        updateSectionData,
        finishOnboarding,
        percentComplete,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
